'use client';

import { useCallback, useMemo, useState, type ReactNode } from 'react';
import { Box } from '@terra/ui-styled-system';
import { ListPageLayout } from '@terra/ui-design-system';
import { HierarchyIcon, Search01Icon } from '@terra/ui-icons';
import { useTranslations } from 'next-intl';
import { useDebounceValue } from 'usehooks-ts';
import { useInternalNetworkStats } from '../../../hooks/api/useInternalNetworkStats';
import { useMainContentTabs } from '../../../store/layoutStore';
import { getInternalNetworkAdEntityKindCount } from '../../../utils/internal-network-graph.utils';
import { EmptyState } from '../../EmptyState';
import { SurfaceAssetsContextNavigation } from './SurfaceAssetsContextNavigation';
import {
  buildCanonicalInternalNetworkAssetTab,
  buildInternalNetworkAssetTab,
} from './internal-network-asset-tabs';
import {
  translateInternalNetworkFilterItems,
  type InternalNetworkAdEntityKindFilter,
} from './surfaceAsset.constants';
import { InternalAssetsTable } from './internal-assets-table/InternalAssetsTable';
import {
  resolveInternalAssetsGroupBy,
  resolveInternalAssetsGroupByOptions,
  type InternalAssetGridItem,
  type InternalAssetsGroupByField,
  type InternalAssetsSort,
} from './internal-assets-table/internal-assets-table.model';
import { useInternalAssetsListData } from './internal-assets-table/useInternalAssetsListData';
import { useInternalAssetsTableRows } from './internal-assets-table/useInternalAssetsTableRows';
import { useInternalAssetsSearchKindCounts } from './internal-assets-table/useInternalAssetsSearchKindCounts';
import { resolveSurfaceAssetKindCount } from './surface-asset-kind-count';

type InternalNetworkAssetsListProps = {
  networkId: string;
  header?: ReactNode;
};

export function InternalNetworkAssetsList({
  networkId,
  header,
}: InternalNetworkAssetsListProps) {
  const t = useTranslations('internalNetwork.assetList');
  const tEntityKinds = useTranslations('internalNetwork.entityKinds');
  const tExternalEntityKinds = useTranslations('externalNetwork.entityKinds');
  const tInternalNetwork = useTranslations('internalNetwork');
  const tAssetDetail = useTranslations('externalNetwork.assetDetail');
  const { navigateInActiveTab } = useMainContentTabs();
  const { stats } = useInternalNetworkStats(networkId);
  const filterItems = useMemo(
    () =>
      translateInternalNetworkFilterItems({
        t: (key) => tEntityKinds(key as Parameters<typeof tEntityKinds>[0]),
        tExternal: (key) =>
          tExternalEntityKinds(
            key as Parameters<typeof tExternalEntityKinds>[0],
          ),
      }),
    [tEntityKinds, tExternalEntityKinds],
  );
  const [selectedKind, setSelectedKind] =
    useState<InternalNetworkAdEntityKindFilter>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sort, setSort] = useState<InternalAssetsSort>(null);
  const [requestedGroupBy, setRequestedGroupBy] =
    useState<InternalAssetsGroupByField | null>(null);
  const [debouncedSearch] = useDebounceValue(searchTerm, 300);
  const mode = selectedKind === 'CANONICAL' ? 'canonical' : 'ad';
  const entityKind = selectedKind === 'CANONICAL' ? null : selectedKind;
  const isAllKinds = selectedKind === null;
  const [sortApplied, setSortApplied] = useState(true);
  const groupBy = resolveInternalAssetsGroupBy({
    mode,
    isAllKinds,
    sortApplied,
    requested: requestedGroupBy,
  });
  const listData = useInternalAssetsListData({
    networkId,
    mode,
    entityKind,
    search: debouncedSearch,
    sort,
    groupBy,
  });

  if (!listData.isLoading && listData.sortApplied !== sortApplied) {
    setSortApplied(listData.sortApplied);

    if (!listData.sortApplied) {
      setSort(null);
    }
  }

  const tableRows = useInternalAssetsTableRows({
    items: listData.items,
    groupBy,
    groups: listData.groups,
    resetKey: `${selectedKind ?? 'ALL'}:${groupBy ?? ''}`,
  });
  const groupByOptions = useMemo(
    () =>
      resolveInternalAssetsGroupByOptions({
        mode,
        isAllKinds,
        sortApplied,
      }).map((option) => ({
        value: option,
        label:
          option === 'isCrownJewel'
            ? tInternalNetwork('crownJewels.title')
            : option === 'isOwned'
              ? tInternalNetwork('ownedPrincipals.title')
              : tAssetDetail('type'),
      })),
    [isAllKinds, mode, sortApplied, tAssetDetail, tInternalNetwork],
  );
  const handleSelectKind = useCallback(
    (kind: InternalNetworkAdEntityKindFilter) => {
      setSelectedKind(kind);
      setSort(null);
    },
    [],
  );
  const handleOpenAsset = useCallback(
    (item: InternalAssetGridItem) => {
      navigateInActiveTab(
        item.source === 'ad'
          ? buildInternalNetworkAssetTab({
              entityKind: item.entity.entityKind,
              entityId: item.entity.nodeId,
              label: item.label,
            })
          : buildCanonicalInternalNetworkAssetTab({
              assetId: item.asset.id,
              label: item.label,
            }),
      );
    },
    [navigateInActiveTab],
  );
  const searchKindCounts = useInternalAssetsSearchKindCounts({
    networkId,
    search: debouncedSearch,
  });
  const getKindCount = useCallback(
    (kind: InternalNetworkAdEntityKindFilter) =>
      resolveSurfaceAssetKindCount({
        kind,
        filteredCounts: searchKindCounts,
        statsCount: getInternalNetworkAdEntityKindCount({ stats, kind }),
      }),
    [searchKindCounts, stats],
  );
  const totalAssetCount = getInternalNetworkAdEntityKindCount({
    stats,
    kind: null,
  });
  const hasActiveFilter =
    selectedKind !== null || debouncedSearch.trim().length > 0;
  const isEmpty =
    !listData.isLoading && !listData.isError && tableRows.rows.length === 0;
  const isAwaitingData = totalAssetCount === 0 && !hasActiveFilter;

  return (
    <ListPageLayout
      header={header}
      navigation={
        <SurfaceAssetsContextNavigation
          items={filterItems}
          selectedKind={selectedKind}
          onSelectKind={handleSelectKind}
          getCount={getKindCount}
          searchTerm={searchTerm}
          onSearchTermChange={setSearchTerm}
          searchPlaceholder={t('searchPlaceholder')}
          onRefresh={listData.refresh}
          isLoading={listData.isLoading}
          groupByOptions={groupByOptions}
          groupBy={groupBy ?? null}
          onGroupByChange={setRequestedGroupBy}
          areAllGroupsCollapsed={tableRows.areAllGroupsCollapsed}
          onExpandAllGroups={tableRows.expandAllGroups}
          onCollapseAllGroups={tableRows.collapseAllGroups}
        />
      }
    >
      {listData.isError ? (
        <Box flex={1} minH={0}>
          <EmptyState
            icon={HierarchyIcon}
            title={t('loadError')}
            description={t('loadErrorDescription')}
          />
        </Box>
      ) : isEmpty ? (
        <Box flex={1} minH={0}>
          <EmptyState
            icon={hasActiveFilter ? Search01Icon : HierarchyIcon}
            title={isAwaitingData ? t('noDataYet') : t('noMatching')}
            description={
              isAwaitingData
                ? t('noDataYetDescription')
                : t('noMatchingDescription')
            }
          />
        </Box>
      ) : (
        <InternalAssetsTable
          mode={mode}
          rows={tableRows.rows}
          loadMoreRowIndex={tableRows.loadMoreRowIndex}
          sort={sort}
          sortApplied={sortApplied}
          collapsedGroupKeys={tableRows.collapsedGroupKeys}
          isLoading={listData.isLoading}
          isFetchingMore={listData.isFetchingMore}
          hasMore={listData.hasMore}
          onLoadMore={listData.loadMore}
          onSortChange={setSort}
          onToggleGroup={tableRows.toggleGroup}
          onOpenAsset={handleOpenAsset}
        />
      )}
    </ListPageLayout>
  );
}
